import type { AuthMethods } from '@api/app'
import { Fingerprint } from 'lucide-react'
import type { ReactNode } from 'react'
import { authClient } from '@web/auth'
import { ProviderMark } from '@layerit/ui/components/provider-mark'
import { Button } from '@layerit/ui/components/ui/button'
import { cn } from '@layerit/ui/lib/utils'
import { publicSiteHref } from '@web/site'

export const PROVIDER_LABELS: Record<string, string> = {
	google: 'Google Workspace',
	microsoft: 'Microsoft 365',
	zoho: 'Zoho Workplace',
}

const PROVIDER_SHORT: Record<string, string> = {
	google: 'Google',
	microsoft: 'Microsoft',
	zoho: 'Zoho',
}

const DESKTOP_COLUMNS = { 1: 'sm:grid-cols-1', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3' } as const

const DESKTOP_SPAN = { 1: 'sm:col-span-1', 2: 'sm:col-span-2', 3: 'sm:col-span-3' } as const

export function PublicSiteLink({ path, children }: { path: string; children: ReactNode }) {
	const href = publicSiteHref(path)
	if (!href) return <span className="text-ink-2">{children}</span>
	return (
		<a className="text-ink-2 underline underline-offset-2 hover:text-ink" href={href} target="_blank" rel="noreferrer">
			{children}
		</a>
	)
}

export function LegalLine({ lead, className }: { lead: string; className?: string }) {
	return (
		<p className={cn('text-xs leading-relaxed text-ink-3', className)}>
			{lead} <PublicSiteLink path="/terms">terms</PublicSiteLink>, <PublicSiteLink path="/privacy">privacy policy</PublicSiteLink>,
			and <PublicSiteLink path="/cookies">cookie policy</PublicSiteLink>.
		</p>
	)
}

interface ProviderButtonsProps {
	methods: AuthMethods
	pending: boolean
	redirectTo: string
	layout: 'row' | 'stack'
	onPasskey?: () => void
}

export function ProviderButtons({ methods, pending, redirectTo, layout, onPasskey }: ProviderButtonsProps) {
	const providers = methods.social
	const stacked = layout === 'stack'
	const showPasskey = methods.passkey && onPasskey !== undefined
	if (providers.length === 0 && !showPasskey) return null

	const count = Math.min(Math.max(providers.length, 1), 3) as 1 | 2 | 3
	const alternatives = providers.length + (showPasskey ? 1 : 0)
	const stranded = (position: number) => !stacked && position === alternatives && alternatives % 2 === 1

	const social = (provider: string, position: number) => (
		<Button
			key={provider}
			type="button"
			disabled={pending}
			aria-label={`Continue with ${PROVIDER_LABELS[provider] ?? provider}`}
			className={cn('gap-2', stacked ? 'h-11 w-full' : 'h-10 sm:col-span-1', stranded(position) && 'col-span-2')}
			onClick={() => void authClient.signIn.social({ provider, callbackURL: redirectTo })}
		>
			<ProviderMark provider={provider} />
			{stacked ? `Continue with ${PROVIDER_LABELS[provider] ?? provider}` : (PROVIDER_SHORT[provider] ?? provider)}
		</Button>
	)

	const passkey = showPasskey ? (
		<Button
			type="button"
			disabled={pending}
			className={cn('h-10 gap-2', stranded(alternatives) && 'col-span-2', !stacked && DESKTOP_SPAN[count])}
			onClick={() => onPasskey?.()}
		>
			<Fingerprint />
			Use a passkey
		</Button>
	) : null

	if (stacked) {
		return (
			<div className="flex flex-col gap-2.5">
				{providers.map((provider, index) => social(provider, index + 1))}
				{passkey}
			</div>
		)
	}

	return (
		<div className={cn('grid grid-cols-2 gap-2', DESKTOP_COLUMNS[count])}>
			{providers.map((provider, index) => social(provider, index + 1))}
			{passkey}
		</div>
	)
}
